import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  ChevronRight,
  Database,
  Search,
  Upload,
  UserPlus,
  BarChart3,
  BookOpen,
  MapPin,
  Leaf,
  Activity,
  Mountain,
  TreePine,
  Layers,
  Pickaxe,
  AlertTriangle,
  Trees,
  Landmark,
  Home,
  Settings,
  Calendar,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  IdCard,
  Tractor,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";

interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  testId: string;
}

interface NavSection {
  label: string;
  icon: LucideIcon;
  color: string;
  items: NavItem[];
  defaultOpen?: boolean;
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: "Mineracao",
    icon: Pickaxe,
    color: "text-amber-500",
    items: [
      { href: "/anm", label: "ANM SIGMINE", icon: Pickaxe, testId: "link-nav-anm" },
      { href: "/cprm", label: "CPRM Geologia", icon: Mountain, testId: "link-nav-cprm" },
      { href: "/sgb", label: "SGB Geologia", icon: Layers, testId: "link-nav-sgb" },
    ],
  },
  {
    label: "Agro & Fundiario",
    icon: Leaf,
    color: "text-green-500",
    items: [
      { href: "/sicar", label: "SICAR", icon: MapPin, testId: "link-nav-sicar" },
      { href: "/sicar-worker", label: "SICAR Worker", icon: Activity, testId: "link-nav-sicar-worker" },
      { href: "/agrofit", label: "AGROFIT", icon: Leaf, testId: "link-nav-agrofit" },
      { href: "/farmway", label: "FarmWay", icon: Tractor, testId: "link-nav-farmway" },
    ],
  },
  {
    label: "Ambiental",
    icon: Trees,
    color: "text-emerald-500",
    items: [
      { href: "/ibama", label: "IBAMA", icon: AlertTriangle, testId: "link-nav-ibama" },
      { href: "/icmbio", label: "ICMBio — UCs", icon: Trees, testId: "link-nav-icmbio" },
      { href: "/funai", label: "FUNAI — TIs", icon: TreePine, testId: "link-nav-funai" },
    ],
  },
  {
    label: "Fiscal",
    icon: Landmark,
    color: "text-blue-500",
    items: [
      { href: "/pgfn", label: "PGFN — Divida Ativa", icon: Landmark, testId: "link-nav-pgfn" },
    ],
  },
  {
    label: "Cadastro",
    icon: IdCard,
    color: "text-purple-500",
    items: [
      { href: "/cardmv-0", label: "CARDMV-0", icon: IdCard, testId: "link-nav-cardmv-0" },
    ],
  },
  {
    label: "Sistema",
    icon: Settings,
    color: "text-muted-foreground",
    items: [
      { href: "/rotinas", label: "Rotinas", icon: Calendar, testId: "link-nav-rotinas" },
      { href: "/docs", label: "API Docs", icon: BookOpen, testId: "link-nav-docs" },
    ],
  },
];

function SidebarSection({ section, collapsed, location }: { section: NavSection; collapsed: boolean; location: string }) {
  const isActive = section.items.some(
    (item) => item.href === location || (item.href !== "/" && location.startsWith(item.href))
  );
  const [open, setOpen] = useState(isActive || section.defaultOpen || false);
  const [prevActive, setPrevActive] = useState(isActive);

  if (isActive && !prevActive) {
    setOpen(true);
  }
  if (isActive !== prevActive) {
    setPrevActive(isActive);
  }

  const Icon = section.icon;

  if (collapsed) {
    return (
      <div className="space-y-0.5 py-1">
        {section.items.map((item) => {
          const ItemIcon = item.icon;
          const active = item.href === location || (item.href !== "/" && location.startsWith(item.href));
          return (
            <Link key={item.href} href={item.href}>
              <div
                className={`flex items-center justify-center h-9 w-9 mx-auto rounded-md transition-colors cursor-pointer ${
                  active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
                title={item.label}
                data-testid={item.testId}
              >
                <ItemIcon className="h-4 w-4" />
              </div>
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div className="py-1">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
        data-testid={`section-${section.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Icon className={`h-3.5 w-3.5 ${section.color}`} />
        <span className="flex-1 text-left">{section.label}</span>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5">
          {section.items.map((item) => {
            const ItemIcon = item.icon;
            const active = item.href === location || (item.href !== "/" && location.startsWith(item.href));
            return (
              <Link key={item.href} href={item.href}>
                <div
                  className={`flex items-center gap-2.5 px-3 py-1.5 ml-2 rounded-md text-sm transition-colors cursor-pointer ${
                    active
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground/80 hover:bg-muted hover:text-foreground"
                  }`}
                  data-testid={item.testId}
                >
                  <ItemIcon className={`h-4 w-4 shrink-0 ${active ? "text-primary" : "text-muted-foreground"}`} />
                  {item.label}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const [location] = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={`flex flex-col bg-card border-r h-screen sticky top-0 transition-all duration-200 ${
        collapsed ? "w-16" : "w-56"
      }`}
      data-testid="sidebar"
    >
      <div className={`flex items-center border-b h-14 px-3 ${collapsed ? "justify-center" : "justify-between"}`}>
        {!collapsed && (
          <Link href="/">
            <span className="text-lg font-bold text-primary cursor-pointer" data-testid="link-home-logo">
              MAVRION HUB
            </span>
          </Link>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          data-testid="button-toggle-sidebar"
          title={collapsed ? "Expandir menu" : "Recolher menu"}
        >
          {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-2 space-y-1">
        {collapsed ? (
          <Link href="/">
            <div
              className={`flex items-center justify-center h-9 w-9 mx-auto rounded-md transition-colors cursor-pointer ${
                location === "/" ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
              title="Dashboard"
              data-testid="link-nav-home"
            >
              <Home className="h-4 w-4" />
            </div>
          </Link>
        ) : (
          <Link href="/">
            <div
              className={`flex items-center gap-2.5 px-3 py-1.5 mx-1 rounded-md text-sm transition-colors cursor-pointer ${
                location === "/"
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-foreground/80 hover:bg-muted hover:text-foreground"
              }`}
              data-testid="link-nav-home"
            >
              <Home className="h-4 w-4 shrink-0" />
              Dashboard
            </div>
          </Link>
        )}

        <div className="px-3 py-1">
          <div className="border-t" />
        </div>

        {NAV_SECTIONS.map((section) => (
          <SidebarSection key={section.label} section={section} collapsed={collapsed} location={location} />
        ))}
      </div>

      <div className="border-t p-3">
        {collapsed ? (
          <button
            onClick={logout}
            className="flex items-center justify-center h-9 w-9 mx-auto rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title="Sair"
            data-testid="button-logout"
          >
            <LogOut className="h-4 w-4" />
          </button>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground truncate" data-testid="text-user-name">
              {user?.name || user?.username}
            </span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="button-logout" title="Sair" className="h-7 w-7 p-0">
              <LogOut className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
}
